import { deleteApi, getApi, patchApi, postApi } from "../helpers/auth_helpers";

const base = () => String(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");

export const fetchProductEditor = (productId: string) =>
  getApi(`${base()}/product/${encodeURIComponent(productId)}/editor`);

export const presignDraftMedia = (productId: string, fileType: string, mediaId: string) =>
  postApi(`${base()}/product/${encodeURIComponent(productId)}/draft/media/presign`, {
    fileType,
    mediaId,
  });

export const persistDraftMedia = (productId: string, body: {
  mediaId: string;
  mediaUrl?: string;
  mediaAssetId?: string;
  section?: string;
  processTitle?: string;
  productProcessId?: string;
  show?: number;
  pin?: number;
  mediaType?: string;
  view?: string;
}) => postApi(`${base()}/product/${encodeURIComponent(productId)}/draft/media`, body);

export const patchDraftMedia = (
  productId: string,
  mediaId: string,
  body: { show?: number; pin?: number; sortOrder?: number; section?: string; processTitle?: string; view?: string }
) =>
  patchApi(
    `${base()}/product/${encodeURIComponent(productId)}/draft/media/${encodeURIComponent(mediaId)}`,
    body
  );

export const deleteDraftMedia = (productId: string, mediaId: string) =>
  deleteApi(
    `${base()}/product/${encodeURIComponent(productId)}/draft/media/${encodeURIComponent(mediaId)}`
  );

export const publishProductDraft = (productId: string) =>
  postApi(`${base()}/product/${encodeURIComponent(productId)}/publish-draft`, {});

const MASTER_REF_KEYS = ["artform", "type", "category", "subcategory", "weavetype"] as const;

export function masterIdFromRef(raw: unknown, depth = 0): string {
  if (raw == null || depth > 6) {
    return "";
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return !trimmed || trimmed === "[object Object]" ? "" : trimmed;
  }
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return "";
  }
  const obj = raw as Record<string, unknown>;
  if (typeof obj.$oid === "string") {
    const oid = obj.$oid.trim();
    return !oid || oid === "[object Object]" ? "" : oid;
  }
  const fromValue = masterIdFromRef(obj.value, depth + 1);
  if (fromValue) {
    return fromValue;
  }
  const fromId = masterIdFromRef(obj._id, depth + 1);
  if (fromId) {
    return fromId;
  }
  return masterIdFromRef(obj.id, depth + 1);
}

export function masterNameFromRef(raw: unknown): string {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return "";
  }
  const obj = raw as Record<string, unknown>;
  if (typeof obj.name === "string") {
    return obj.name;
  }
  if (typeof obj.label === "string") {
    return obj.label;
  }
  return "";
}

function canonicalMasterRef(raw: unknown): { _id: string; name: string } {
  return { _id: masterIdFromRef(raw), name: masterNameFromRef(raw) };
}

function withCanonicalMasterRefs(product: any): any {
  if (!product || typeof product !== "object") {
    return product;
  }
  const out = { ...product };
  MASTER_REF_KEYS.forEach((key) => {
    if (out[key] != null) {
      out[key] = canonicalMasterRef(out[key]);
    }
  });
  return out;
}

export function masterIdsForProductWrite(form: any) {
  return {
    artformId: masterIdFromRef(form?.artform),
    typeId: masterIdFromRef(form?.type),
    categoryId: masterIdFromRef(form?.category),
    subcategoryId: masterIdFromRef(form?.subCategory),
    weavetypeId: masterIdFromRef(form?.weaveType),
  };
}

export const applyDraftOverlay = (product: any): any => {
  if (!product?.hasDraftChanges || !product?.draft || typeof product.draft !== "object") {
    return withCanonicalMasterRefs(product);
  }
  const overlay: any = { ...product };
  Object.entries(product.draft).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      overlay[key] = value;
    }
  });
  return withCanonicalMasterRefs(overlay);
};

